import { NavigationProvider, ThemeProvider } from "@gearbox-protocol/ui-kit";
import { ConnectKitWalletAdapter } from "@gearbox-protocol/ui-kit/connectkit";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConnectKitProvider } from "connectkit";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { WagmiProvider } from "wagmi";
import { connect as wagmiConnect } from "wagmi/actions";
import { config, createMockConfig } from "@/config/wagmi";
import { AppLayout } from "./app-layout";

const IS_E2E = import.meta.env.VITE_E2E === "true";

declare global {
  interface Window {
    _e2eConnect?: (
      privateKeyOrAddress: `0x${string}`,
    ) => Promise<`0x${string}`>;
  }
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  });
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => makeQueryClient());
  const [wagmiConfig, setWagmiConfig] = useState(config);

  useEffect(() => {
    if (!IS_E2E) return;

    window._e2eConnect = async privateKeyOrAddress => {
      const newConfig = createMockConfig(privateKeyOrAddress);
      const result = await wagmiConnect(newConfig, {
        connector: newConfig.connectors[0],
      });
      setWagmiConfig(newConfig);
      return result.accounts[0];
    };

    return () => {
      delete window._e2eConnect;
    };
  }, []);

  return (
    <ThemeProvider defaultTheme="dark">
      <WagmiProvider config={wagmiConfig} reconnectOnMount={true}>
        <QueryClientProvider client={queryClient}>
          <ConnectKitProvider>
            <ConnectKitWalletAdapter>
              <NavigationProvider
                Link={Link}
                useLocation={useLocation}
                useNavigate={useNavigate}
              >
                <AppLayout>{children}</AppLayout>
              </NavigationProvider>
            </ConnectKitWalletAdapter>
          </ConnectKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </ThemeProvider>
  );
}
